import React, { useState, useRef, useEffect, useCallback } from 'react';
import { chatWithAgent } from './helpers-goPilot';
import AgentPopupHeader from './components/AgentPopupHeader';
import AgentPopupInput from './components/AgentPopupInput';
import './GoFormzAgentPanel.scss';
import ChatMessages from './components/ChatMessages';
import { ConversationMessage, ConversationSessionDocument, MessageVote } from './goFormz-agent-types';
import { useGoFormzAgent } from './GoFormzAgentHook';
import AgentPopupSuggestions from './components/AgentPopupSuggestions';
import { analyticsTrack } from '../helpers/AnalyticsUtility';
import { useAppSelector } from '../hooks/ReduxHooks';
import { getTemplateState } from '../templateeditor/selectors';

export type DockPosition = 'right' | 'left' | 'top' | 'bottom';
export type DockMode = 'inline' | 'overlay';

interface GoFormzAgentPanelProps {
  formulaSelected?: (formula: string) => void;
  defaultWidth?: number;
  defaultHeight?: number;
  minWidth?: number;
  minHeight?: number;
  maxWidth?: number;
  maxHeight?: number;
}

export default function GoFormzAgentPanel({
  formulaSelected,
  defaultWidth = 450,
  defaultHeight = 400,
  minWidth = 320,
  minHeight = 300,
  maxWidth = 600,
  maxHeight = 800
}: GoFormzAgentPanelProps) {
  const { isAgentOpen, closeAgent, templateContext, selectFormula, selectedFormula, dockPosition, dockMode, setPanelSize } = useGoFormzAgent();

  // Get template state - this will be current at render time
  const template = useAppSelector((state) => getTemplateState(state).template);

  const [aiSupportQuery, setAiSupportQuery] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [messages, setMessages] = useState<ConversationMessage[]>([]);
  const bodyRef = useRef<HTMLDivElement>(null);
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [readOnly, setReadOnly] = useState(false);
  const [isClosing, setIsClosing] = useState(false);
  const [previousPosition, setPreviousPosition] = useState<DockPosition>(dockPosition);
  const [topOffset, setTopOffset] = useState(55); // Default navbar height
  const wasAgentOpenRef = useRef(isAgentOpen);

  // Panel state
  const [localPanelSize, setLocalPanelSize] = useState({
    width: defaultWidth,
    height: defaultHeight
  });
  const [isResizing, setIsResizing] = useState(false);
  const resizeRef = useRef<HTMLDivElement>(null);

  // Calculate top offset to account for banners and navbar - same logic as calculation builder
  useEffect(() => {
    const calculateTopOffset = () => {
      let offset = 0;

      // Check for banners that appear above the navbar
      const specialAccessBanner = document.querySelector('.special-access-banner');
      if (specialAccessBanner) {
        offset += specialAccessBanner.getBoundingClientRect().height;
      }

      const notificationBanners = document.querySelectorAll('.notification-banner');
      notificationBanners.forEach((banner) => {
        offset += banner.getBoundingClientRect().height;
      });

      // Add navbar height
      const navbar = document.querySelector('.main-menu');
      if (navbar) {
        offset += navbar.getBoundingClientRect().height;
      } else {
        offset += 55; // fallback to default navbar height
      }

      return Math.max(offset, 55); // Ensure minimum offset for navbar
    };

    const updateOffset = () => {
      const newOffset = calculateTopOffset();
      if (newOffset !== topOffset) {
        setTopOffset(newOffset);
      }
    };

    // Initial calculation
    updateOffset();

    // Recalculate when banners might appear/disappear
    // Use a debounced approach to avoid excessive calculations
    let timeoutId: NodeJS.Timeout;
    const debouncedUpdate = () => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(updateOffset, 100);
    };

    const observer = new MutationObserver(debouncedUpdate);
    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['class', 'style']
    });

    // Also recalculate on resize
    window.addEventListener('resize', debouncedUpdate);

    return () => {
      clearTimeout(timeoutId);
      observer.disconnect();
      window.removeEventListener('resize', debouncedUpdate);
    };
  }, [topOffset]);

  const close = () => {
    // Always clear the conversation when closing the agent
    clearAgent();

    if (dockMode === 'overlay') {
      setIsClosing(true);
      setTimeout(() => {
        closeAgent();
        setIsClosing(false);
      }, 300);
    } else {
      closeAgent();
    }
  };

  // Handle position changes with animation
  useEffect(() => {
    if (dockPosition !== previousPosition) {
      setPreviousPosition(dockPosition);
    }
  }, [dockPosition, previousPosition]);

  // Removed: Previous conversation restoration - we want fresh conversations each time

  const clearAgent = useCallback(() => {
    setReadOnly(false);
    setMessages([]);
    setAiSupportQuery('');
    setSessionId(null);
    localStorage.removeItem('AI_SESSION_ID');
    selectFormula(null);
  }, [selectFormula]);

  // Handle state changes from the hook - only clear conversation when agent opens/closes
  useEffect(() => {
    const wasOpen = wasAgentOpenRef.current;
    wasAgentOpenRef.current = isAgentOpen;

    if (isAgentOpen && !wasOpen) {
      // Agent just opened - clear any existing conversation
      setMessages([]);
      setSessionId(null);
      localStorage.removeItem('AI_SESSION_ID');
      setReadOnly(false);

      if (bodyRef.current) {
        bodyRef.current.scrollTop = bodyRef.current.scrollHeight;
      }

      sendAnalyticsEvent('AI Formula Builder Clicked', {
        value: templateContext.selectedProperty,
        category: 'GoPilotEvent'
      });
    } else if (!isAgentOpen && wasOpen) {
      // Agent just closed - clear the conversation
      setReadOnly(false);
      setMessages([]);
      setAiSupportQuery('');
      setSessionId(null);
      localStorage.removeItem('AI_SESSION_ID');
      selectFormula(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAgentOpen]); // Only depend on isAgentOpen to avoid clearing during conversation

  // Handle formula selection from other components
  useEffect(() => {
    if (selectedFormula && formulaSelected) {
      formulaSelected(selectedFormula);
    }
  }, [selectedFormula, formulaSelected]);

  // Scroll to bottom whenever messages are added
  useEffect(() => {
    if (bodyRef.current) {
      bodyRef.current.scrollTop = bodyRef.current.scrollHeight;
    }
  }, [messages]);

  // Handle resize
  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    setIsResizing(true);
  }, []);

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!isResizing) return;

      const newSize = { ...localPanelSize };

      if (dockPosition === 'right' || dockPosition === 'left') {
        const newWidth = dockPosition === 'right' ? window.innerWidth - e.clientX : e.clientX;
        newSize.width = Math.min(Math.max(newWidth, minWidth), maxWidth);
      } else {
        const newHeight = dockPosition === 'bottom' ? window.innerHeight - e.clientY : e.clientY;
        newSize.height = Math.min(Math.max(newHeight, minHeight), maxHeight);
      }

      setLocalPanelSize(newSize);
      setPanelSize(newSize); // Update the context
    };

    const handleMouseUp = () => {
      setIsResizing(false);
    };

    if (isResizing) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isResizing, dockPosition, localPanelSize, minWidth, maxWidth, minHeight, maxHeight, setPanelSize]);

  const sendAnalyticsEvent = (eventName: string, properties: Record<string, unknown>) => {
    try {
      analyticsTrack(eventName, properties);
    } catch (error) {
      //do nothing if analytics fails
    }
  };

  const sendMessage = useCallback(
    async (queryOverride?: string) => {
      const query = typeof queryOverride === 'string' ? queryOverride.trim() : aiSupportQuery.trim();
      if (!query) return;

      setAiSupportQuery('');
      const userMessage: ConversationMessage = {
        agentMessage: false,
        message: query,
        vote: MessageVote.None,
        messageId: ''
      };

      setMessages((prev) => [...prev, userMessage]);
      setIsSending(true);
      sendAnalyticsEvent('GoPilot - Sent Message', {
        category: 'GoPilotEvent',
        query
      });
      try {
        // Safely access templateContext with fallbacks to prevent errors
        const selectedFieldIds = templateContext?.selectedFieldIds || [];
        const selectedFieldId = selectedFieldIds.length > 0 ? selectedFieldIds[0] : null;

        // Debug logging for development
        if (process.env.NODE_ENV === 'development') {
          console.log('GoFormzAgentPanel - Sending message with context:', {
            templateId: templateContext?.templateId,
            selectedProperty: templateContext?.selectedProperty,
            selectedCalculation: templateContext?.selectedCalculation,
            query
          });
        }

        // Use all the available context fields from templateContext
        const agentResponse = await chatWithAgent(
          templateContext?.templateId || null,
          template,
          templateContext?.currentPageId || null,
          templateContext?.selectedDataLayoutId || null,
          selectedFieldId,
          templateContext?.selectedProperty || null,
          templateContext?.selectedCalculation || null,
          query,
          sessionId
        );

        // Update session ID from response and save to localStorage
        if (agentResponse.conversationSessionId != sessionId) {
          setSessionId(agentResponse.conversationSessionId);
          localStorage.setItem('AI_SESSION_ID', agentResponse.conversationSessionId);
        }

        setMessages((prev) => [...prev, agentResponse.agentMessage]);

        // Note: Template context updates were causing conversation clearing
        // Removing unnecessary template context updates for now
        // Only update template context if there are specific changes needed
      } catch (error) {
        console.error('Error communicating with agent:', error);
        const errorMessage: ConversationMessage = {
          agentMessage: true,
          message: 'Something went wrong. Please try again.',
          messageId: '',
          vote: MessageVote.None
        };
        setMessages((prev) => [...prev, errorMessage]);
      } finally {
        setIsSending(false);
      }
    },
    [aiSupportQuery, sessionId, templateContext, template]
  );

  const handleKeyDown = (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      sendMessage();
    }
  };

  // Handle local formula selection and propagate through the hook
  const handleFormulaSelected = useCallback(
    (formula: string) => {
      if (readOnly) return;
      selectFormula(formula);
      if (formulaSelected) {
        formulaSelected(formula);
      }
    },
    [selectFormula, formulaSelected, readOnly]
  );

  const historyToggled = async (showing: boolean) => {
    setHistoryOpen(showing);
  };

  const historicalConversationSelected = async (conversation: ConversationSessionDocument) => {
    setHistoryOpen(false);
    setMessages(conversation.messages);
    setSessionId(conversation.sessionId);
    setReadOnly(true);
    localStorage.setItem('AI_SESSION_ID', conversation.sessionId);
  };

  const sendPromptForUser = async (key: string) => {
    await sendMessage(key);
  };

  if (!isAgentOpen) return null;

  const getPanelStyles = () => {
    const isHorizontal = dockPosition === 'top' || dockPosition === 'bottom';
    const styles: React.CSSProperties = {
      position: dockMode === 'overlay' ? 'fixed' : 'static', // Use static for inline to ensure proper flex behavior
      zIndex: dockMode === 'overlay' ? 10000 : undefined
    };

    if (dockMode === 'overlay') {
      // For overlay mode, always position from the specified edge
      // But respect the navbar height and any banners for top positioning
      styles[dockPosition] = 0;

      if (dockPosition === 'left' || dockPosition === 'right') {
        styles.top = topOffset; // Start below the navbar and banners
        styles.bottom = 0;
        styles.width = localPanelSize.width;
      } else {
        styles.left = 0;
        styles.right = 0;
        styles.height = localPanelSize.height;

        if (dockPosition === 'top') {
          styles.top = topOffset; // Start below the navbar and banners for top positioning
        }
        // bottom positioning doesn't need navbar offset
      }

      // Add closing animation styles
      if (isClosing) {
        styles.opacity = 0;
        if (dockPosition === 'right') {
          styles.transform = 'translateX(100%)';
        } else if (dockPosition === 'left') {
          styles.transform = 'translateX(-100%)';
        } else if (dockPosition === 'top') {
          styles.transform = 'translateY(-100%)';
        } else if (dockPosition === 'bottom') {
          styles.transform = 'translateY(100%)';
        }
      }
    } else {
      // For inline mode
      if (isHorizontal) {
        styles.width = '100%';
        styles.height = localPanelSize.height;
        // For inline mode, ensure the panel has a minimum height
        styles.minHeight = '200px';
        styles.maxHeight = '80vh';
      } else {
        styles.width = localPanelSize.width;
        styles.height = '100%';
        // For inline mode, ensure the panel has a minimum width
        styles.minWidth = '200px';
        styles.maxWidth = '50vw';
      }
    }

    // Debug logging for development
    if (process.env.NODE_ENV === 'development') {
      console.log('GoPilot Agent Panel Debug:', {
        dockPosition,
        dockMode,
        localPanelSize,
        topOffset,
        stylesApplied: styles
      });
    }

    return styles;
  };

  const getResizeHandleStyles = () => {
    const styles: React.CSSProperties = {
      position: 'absolute',
      backgroundColor: 'transparent',
      zIndex: 10
    };

    // For inline mode, we might need different positioning
    if (dockMode === 'inline') {
      styles.position = 'absolute'; // Keep absolute for resize handle even in inline mode
    }

    if (dockPosition === 'right') {
      styles.left = 0;
      styles.top = 0;
      styles.bottom = 0;
      styles.width = '4px';
      styles.cursor = 'ew-resize';
    } else if (dockPosition === 'left') {
      styles.right = 0;
      styles.top = 0;
      styles.bottom = 0;
      styles.width = '4px';
      styles.cursor = 'ew-resize';
    } else if (dockPosition === 'top') {
      styles.bottom = 0;
      styles.left = 0;
      styles.right = 0;
      styles.height = '4px';
      styles.cursor = 'ns-resize';
    } else if (dockPosition === 'bottom') {
      styles.top = 0;
      styles.left = 0;
      styles.right = 0;
      styles.height = '4px';
      styles.cursor = 'ns-resize';
    }

    return styles;
  };

  return (
    <div
      className={`ai-agent-panel ai-agent-panel--${dockPosition} ai-agent-panel--${dockMode}`}
      style={
        {
          ...getPanelStyles(),
          '--panel-width': `${localPanelSize.width}px`,
          '--panel-height': `${localPanelSize.height}px`,
          '--top-offset': `${topOffset}px`,
          '--agent-width': `${localPanelSize.width}px`,
          '--agent-height': `${localPanelSize.height}px`
        } as React.CSSProperties
      }
    >
      <div ref={resizeRef} className="ai-agent-panel__resize-handle" style={getResizeHandleStyles()} onMouseDown={handleMouseDown} />
      <div className="ai-popup-content">
        <AgentPopupHeader
          onClose={close}
          onClear={clearAgent}
          historyToggled={historyToggled}
          historicalConversationSelected={historicalConversationSelected}
        />
        {!historyOpen && (
          <>
            <AgentPopupSuggestions sendPromptForUser={sendPromptForUser} />
            <div className="ai-popup-body" ref={bodyRef}>
              <ChatMessages sessionId={sessionId} messages={messages} formulaSelected={handleFormulaSelected} />
            </div>
            {!readOnly && (
              <AgentPopupInput value={aiSupportQuery} onChange={setAiSupportQuery} onKeyDown={handleKeyDown} onSend={sendMessage} disabled={isSending} />
            )}
          </>
        )}
      </div>
    </div>
  );
}
